import { useCallback } from 'react';
import { useTranslation } from '@/i18n';
import { useExport } from '@/hooks/ui/useExport';
import { useUI } from '@/hooks/ui/useUI';
import { roleAssignedEmployeesService } from '@/api/domains/authorization';
import type { RoleAssignedEmployeesExportFilters } from '@/api/domains/authorization';
import type { UIExportFormat } from '@/types/fileExport.types';


export interface UseRoleAssignedEmployeesExportResult {
  /** Descarga a Excel el detalle de roles web y APP móvil asignados. Solo formato EXCEL. */
  exportAssignments: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
}

/**
 * Descarga a Excel el detalle de roles asignados de la pestaña Asignados, aplicando los mismos
 * filtros (estado y búsqueda) vigentes en el listado.
 */
export function useRoleAssignedEmployeesExport(
  filters: RoleAssignedEmployeesExportFilters,
): UseRoleAssignedEmployeesExportResult {
  const { t: translate } = useTranslation();
  const { showError } = useUI();

  const { onExport: exportFile, isExporting } = useExport({
    exportFn: () => roleAssignedEmployeesService.export(filters),
    successMessage: translate('authorization:assigned.messages.exportSuccess'),
    errorMessage: translate('authorization:assigned.messages.exportError'),
  });

  const exportAssignments = useCallback(
    async (format: UIExportFormat) => {
      if (format !== 'EXCEL') {
        showError(translate('authorization:assigned.messages.exportError'));
        return;
      }
      await exportFile();
    },
    [exportFile, showError, translate],
  );

  return { exportAssignments, isExporting };
}

export default useRoleAssignedEmployeesExport;
